/**
 * @typedef CalculationResult
 * @type {object}
 * @property {object} allocatedComponents - Components allocated for this variant
 * @property {number} calculatedQuantityAvailable - Quantity available/allocated for this variant
 */

/**
 *
 * @param {object} param
 * @param {object} param.allocatedComponents - Object of any previously allocated components. Format matches returned allocatedComponents.
 * @param {?Component[]} param.components - Components required for this variant, if any
 * @param {number} param.quantityAvailable - Quantity available for this variant. Used when no components are provided.
 * @param {number} param.quantityRequested - Quantity requested for this variant. Used to allocate components when less than max required.
 *
 *
 * @returns {CalculationResult}
 */

export default function calculateQuantityAvailable({
  allocatedComponents = {},
  components,
  quantityAvailable,
  quantityRequested,
}) {
  const adjustedAllocatedComponents = {
    ...allocatedComponents,
  };

  if (!components?.length)
    return {
      allocatedComponents: adjustedAllocatedComponents,
      calculatedQuantityAvailable: quantityAvailable,
    };

  const allocatedQty = (id) => {
    return id in allocatedComponents ? allocatedComponents[id] : 0;
  };

  const componentMultiplesAvailable = components.map(
    ({ id, quantityAvailable, quantityEach }) => {
      return {
        id,
        multiplesAvailable:
          Math.floor(quantityAvailable / quantityEach) - allocatedQty(id),
        totalMultiplesAvailable: Math.floor(quantityAvailable / quantityEach),
      };
    },
  );

  const limitingComponent = componentMultiplesAvailable.sort(
    (a, b) => a.multiplesAvailable - b.multiplesAvailable,
  )[0];

  const calculatedQuantityAllocated = quantityRequested
    ? Math.min(quantityRequested, limitingComponent.multiplesAvailable)
    : limitingComponent.multiplesAvailable;

  components.forEach(({ id, quantityEach }) => {
    adjustedAllocatedComponents[id] =
      allocatedQty(id) + quantityEach * calculatedQuantityAllocated;
  });

  return {
    allocatedComponents: adjustedAllocatedComponents,
    calculatedQuantityAvailable: limitingComponent.multiplesAvailable,
    totalQuantityAvailable: limitingComponent.totalMultiplesAvailable,
  };
}
